import { ChangeDetectorRef, Component } from '@angular/core';
import { GlobalService } from '../../_services/global.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DatasearchService } from '../../_services/datasearch.service';
import { BusyService } from '../../_services/busy.service';
import { DatePipe, NgFor, NgIf, UpperCasePipe, NgClass } from '@angular/common';
import { HeaderComponent } from '../../components/header/header.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';

@Component({
  selector: 'app-patent-categories',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    DatePipe,
    UpperCasePipe,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    NgClass,
  ],
  templateUrl: './patent-categories.component.html',
  styleUrl: './patent-categories.component.scss',
})
export class PatentCategoriesComponent {
  catDetails: any = {};
  newsList: any = [];
  tableName = 'NEWS';
  filePath = 'https://adminbackend.chemrobotics.com/uploads/news_images/';
  isSubcategory = false;
  categoryName = '';
  databaseId: string = ''; // Store database ID for filtering
  heading = '';
  toggle_sidebar = false;

  constructor(private globalService: GlobalService, private route: ActivatedRoute,
    private busyService: BusyService,
    private datasearch: DatasearchService,
    private router: Router,
    private cdref: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.globalService.sendMenuIndex(1);
    const categoryId = this.route.snapshot.params['catid'];
    console.log('Category/Subcategory ID from route:', categoryId);

    // Get database ID from route query params or use default
    this.route.queryParams.subscribe(params => {
      this.databaseId = params['databaseId'] || '';
      console.log('Database ID from query params:', this.databaseId);
    });

    // First, try to determine if this is a category or subcategory
    this.getCategoryDetails(categoryId);
    this.getNewsList(categoryId);
  }

  getCategoryDetails(id: string) {
    // Fetch category tree to get details
    this.datasearch.getCatSubcatTree(id).subscribe(
      (res: any) => {
        const categories = res.data.categories;

        // First, check if it's a category
        const matchedCategory = categories.find((cat: any) => cat._id === id);

        if (matchedCategory) {
          // It's a category
          this.isSubcategory = false;
          this.catDetails = matchedCategory;
          this.categoryName = matchedCategory.name;
          console.log('✅ Category Details:', this.catDetails);
        } else {
          // Check if it's a subcategory
          let foundSubcategory = null;
          let parentCategory = null;

          for (const cat of categories) {
            if (cat.subcategories && Array.isArray(cat.subcategories)) {
              const subcat = cat.subcategories.find((sub: any) => sub._id === id);
              if (subcat) {
                foundSubcategory = subcat;
                parentCategory = cat;
                break;
              }
            }
          }

          if (foundSubcategory) {
            // It's a subcategory
            this.isSubcategory = true;
            this.catDetails = foundSubcategory;
            this.categoryName = `${parentCategory.name} > ${foundSubcategory.name}`;
            console.log('✅ Subcategory Details:', this.catDetails);
          } else {
            console.warn('⚠️ Category/Subcategory not found for ID:', id);
          }
        }
      },
      (errorobj: any) => {
        console.error('❌ Error fetching category details:', errorobj);
      }
    );
  }

  getNewsList(id: string) {
    this.busyService.busy();

    // We'll try category first, then subcategory if needed
    // A better approach would be to wait for getCategoryDetails to complete
    // For now, we'll use a simple approach: try category API
    this.datasearch.getNewsByCategory(id).subscribe(
      (res: any) => {
        this.busyService.idle();

        const newsData = res.data?.news || [];

        // If database ID not set from query params, try to extract from first news item
        if (!this.databaseId && newsData.length > 0) {
          const firstNews = newsData[0];
          if (firstNews.database_meta && Array.isArray(firstNews.database_meta)) {
            this.databaseId = firstNews.database_meta[0]?.databaseId || '';
            console.log('📂 Extracted database ID from news:', this.databaseId);
          }
        }

        if (newsData.length > 0) {
          // Successfully got news from category API - filter by databaseId
          this.newsList = this.filterNewsByDatabaseId(newsData);
          this.addImagePaths();
          console.log('📰 Category News List (filtered):', this.newsList);
        } else {
          // Try subcategory API
          this.getSubcategoryNews(id);
        }
      },
      (errorobj: any) => {
        // If category API fails, try subcategory API
        console.log('Category API failed, trying subcategory API...');
        this.getSubcategoryNews(id);
      }
    );
  }

  getSubcategoryNews(subcategoryId: string) {
    this.datasearch.getNewsBySubcategory(subcategoryId).subscribe(
      (res: any) => {
        this.busyService.idle();

        const newsData = res.data?.news || [];

        // If database ID not set from query params, try to extract from first news item
        if (!this.databaseId && newsData.length > 0) {
          const firstNews = newsData[0];
          if (firstNews.database_meta && Array.isArray(firstNews.database_meta)) {
            this.databaseId = firstNews.database_meta[0]?.databaseId || '';
            console.log('📂 Extracted database ID from subcategory news:', this.databaseId);
          }
        }

        // Filter subcategory news by databaseId
        this.newsList = this.filterNewsByDatabaseId(newsData);
        this.addImagePaths();
        console.log('📰 Subcategory News List (filtered):', this.newsList);
      },
      (errorobj: any) => {
        this.busyService.idle();
        console.error('❌ Error fetching subcategory news:', errorobj);
      }
    );
  }

  filterNewsByDatabaseId(newsData: any[]): any[] {
    if (!this.databaseId) {
      console.warn('⚠️ No database ID set, returning all news');
      return newsData;
    }

    console.log(`🔍 Filtering ${newsData.length} news items by database ID: ${this.databaseId}`);

    const filtered = newsData.filter((news: any) => {
      if (news.database_meta && Array.isArray(news.database_meta)) {
        const hasMatchingDatabase = news.database_meta.some((meta: any) =>
          meta.databaseId === this.databaseId
        );

        if (!hasMatchingDatabase) {
          console.log(`  ⏭️ Skipped news "${news.title}" (no matching databaseId)`);
        }

        return hasMatchingDatabase;
      }
      console.warn(`  ⚠️ News "${news.title}" has no database_meta`);
      return false;
    });

    console.log(`✅ Filtered to ${filtered.length} news items`);
    return filtered;
  }

  addImagePaths() {
    this.newsList.forEach((val: any) => {
      val.imgPath = this.filePath + val.thumbnailImage;
    });
  }

  goToNewsDetail(_id: any) {
    this.router.navigate(['/discovery-updates', _id]);
  }

  goBackTocategories(): void {
    this.router.navigate(['/discovery']);
  }

  ngAfterViewInit() {
    this.cdref.detectChanges();
  }

  receiveHeading(event: string) {
    this.heading = event;
    this.cdref.detectChanges();
  }

  receiveSidebarEvent(event: boolean) {
    this.toggle_sidebar = event;
    this.cdref.detectChanges();
  }
}
